import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/ErrorBoundary";
import FernetBackupModal from "@/components/FernetBackupModal";
import Login from "@/pages/Login";
import PasswordGate from "@/pages/PasswordGate";
import EntityDetailPage from "@/pages/EntityDetailPage";
import AppShell from "@/layout/AppShell";
import Dashboard from "@/pages/Dashboard";
import Knowledge from "@/pages/Knowledge";
import KnowledgeBase from "@/pages/KnowledgeBase";
import Universe from "@/pages/Universe";
import Articles from "@/pages/Articles";
import Editorial from "@/pages/Editorial";
import DailyBriefs from "@/pages/DailyBriefs";
import DailyBriefDetail from "@/pages/DailyBriefDetail";
import WeeklyBrief from "@/pages/WeeklyBrief";
import WeeklyBriefDetail from "@/pages/WeeklyBriefDetail";
import MonthlyBrief from "@/pages/MonthlyBrief";
import MonthlyBriefs from "@/pages/MonthlyBriefs";
import MonthlyBriefDetail from "@/pages/MonthlyBriefDetail";
import WeeklyQuotesList from "@/pages/WeeklyQuotesList";
import WeeklyQuotesDetail from "@/pages/WeeklyQuotesDetail";
import WeeklySurveysList from "@/pages/WeeklySurveysList";
import WeeklySurveyDetail from "@/pages/WeeklySurveyDetail";
import WeeklyNumbersList from "@/pages/WeeklyNumbersList";
import WeeklyNumberDetail from "@/pages/WeeklyNumberDetail";
import MonthlyTrends from "@/pages/MonthlyTrends";
import PerEventRecap from "@/pages/PerEventRecap";
import QuarterlyStateOfIndustry from "@/pages/QuarterlyStateOfIndustry";
import MonthlyPulse from "@/pages/MonthlyPulse";
import WingmanChat from "@/pages/WingmanChat";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Memory from "@/pages/Memory";
import Agents from "@/pages/Agents";
import Workflows from "@/pages/Workflows";
import Logs from "@/pages/Logs";
import Settings from "@/pages/Settings";
import McpAdmin from "@/pages/McpAdmin";
import Experiments from "@/pages/Experiments";
import ExperimentsCompare from "@/pages/ExperimentsCompare";
import NotFound from "@/pages/NotFound";
import { API, authHeaders } from "@/lib/api";

function Shelled({ children }) {
  return <AppShell>{children}</AppShell>;
}

function AuthedExtras() {
  const [user, setUser] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/auth/me`, { headers: authHeaders() });
        if (r.ok) setUser(await r.json());
      } catch {
        /* ignore */
      }
    })();
  }, []);
  // Modal disabled per user request — component file kept for later re-enable.
  // return <FernetBackupModal user={user} />;
  return null;
}

function Router() {
  const location = useLocation();
  const showExtras = location.pathname !== "/login";
  return (
    <>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Shelled><Dashboard /></Shelled>} />
        <Route path="/knowledge" element={<Shelled><Knowledge /></Shelled>} />
        <Route path="/knowledge-base" element={<Shelled><KnowledgeBase /></Shelled>} />
        {/* Universe — unified parent page for Companies + Sources */}
        <Route path="/universe" element={<Navigate to="/universe/companies" replace />} />
        <Route path="/universe/companies" element={<Shelled><Universe /></Shelled>} />
        <Route path="/universe/sources" element={<Shelled><Universe /></Shelled>} />
        {/* Legacy routes → redirect to the unified Universe URLs. */}
        <Route path="/companies" element={<Navigate to="/universe/companies" replace />} />
        <Route path="/sources" element={<Navigate to="/universe/sources" replace />} />
        {/* Detail pages keep their own top-level route (drills below the Universe grid). */}
        <Route path="/companies/:slug" element={<Shelled><EntityDetailPage kind="company" /></Shelled>} />
        <Route path="/topics/:slug" element={<Shelled><EntityDetailPage kind="topic" /></Shelled>} />
        <Route path="/markets/:slug" element={<Shelled><EntityDetailPage kind="market" /></Shelled>} />
        <Route path="/articles" element={<Navigate to="/editorial" replace />} />
        <Route path="/editorial" element={<Shelled><Editorial /></Shelled>} />
        <Route path="/daily-briefs" element={<Shelled><DailyBriefs /></Shelled>} />
        <Route path="/daily-briefs/:id" element={<Shelled><DailyBriefDetail /></Shelled>} />
        <Route path="/weekly-brief" element={<Shelled><WeeklyBrief /></Shelled>} />
        <Route path="/weekly-briefs" element={<Shelled><WeeklyBrief /></Shelled>} />
        <Route path="/weekly-briefs/:id" element={<Shelled><WeeklyBriefDetail /></Shelled>} />
        <Route path="/monthly-brief" element={<Navigate to="/monthly-briefs" replace />} />
        <Route path="/monthly-briefs" element={<Shelled><MonthlyBriefs /></Shelled>} />
        <Route path="/monthly-briefs/:id" element={<Shelled><MonthlyBriefDetail /></Shelled>} />
        <Route path="/monthly-brief-legacy" element={<Shelled><MonthlyBrief /></Shelled>} />
        <Route path="/weekly-quotes" element={<Shelled><WeeklyQuotesList /></Shelled>} />
        <Route path="/weekly-quotes/:id" element={<Shelled><WeeklyQuotesDetail /></Shelled>} />
        <Route path="/weekly-surveys" element={<Shelled><WeeklySurveysList /></Shelled>} />
        <Route path="/weekly-surveys/:id" element={<Shelled><WeeklySurveyDetail /></Shelled>} />
        <Route path="/weekly-numbers" element={<Shelled><WeeklyNumbersList /></Shelled>} />
        <Route path="/weekly-numbers/:date" element={<Shelled><WeeklyNumberDetail /></Shelled>} />
        <Route path="/monthly-trends" element={<Shelled><MonthlyTrends /></Shelled>} />
        <Route path="/per-event-recap" element={<Shelled><PerEventRecap /></Shelled>} />
        <Route path="/quarterly-state-industry" element={<Shelled><QuarterlyStateOfIndustry /></Shelled>} />
        <Route path="/monthly-pulse" element={<Shelled><MonthlyPulse /></Shelled>} />
        <Route path="/chat" element={<Shelled><WingmanChat /></Shelled>} />
        <Route path="/memory" element={<Shelled><Memory /></Shelled>} />
        <Route path="/agents" element={<Shelled><Agents /></Shelled>} />
        <Route path="/workflows" element={<Shelled><Workflows /></Shelled>} />
        <Route path="/logs" element={<Shelled><Logs /></Shelled>} />
        <Route path="/settings" element={<Shelled><Settings /></Shelled>} />
        <Route path="/mcp-admin" element={<Shelled><McpAdmin /></Shelled>} />
        <Route path="/experiments" element={<Shelled><Experiments /></Shelled>} />
        <Route path="/experiments/compare" element={<Shelled><ExperimentsCompare /></Shelled>} />
        <Route path="*" element={<Shelled><NotFound /></Shelled>} />
      </Routes>
      {showExtras && <AuthedExtras />}
    </>
  );
}

export default function App() {
  // Private-beta gate: block the entire app tree behind a signed cookie
  // (POST /api/access/verify). The Google OAuth flow lives BEHIND this
  // gate — once granted, the normal Router mounts and Login/Auth continues
  // unchanged.
  const [gateGranted, setGateGranted] = useState(false);
  return (
    <ErrorBoundary>
      {!gateGranted && <PasswordGate onGranted={() => setGateGranted(true)} />}
      {gateGranted && (
        <BrowserRouter>
          <Router />
          <Toaster theme="light" richColors position="top-right" />
        </BrowserRouter>
      )}
    </ErrorBoundary>
  );
}
